import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle, Clock, Eye, Download, RefreshCw, Filter } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import axios from 'axios';

const API_URL = '';

const DepositManagement = () => {
  const { toast } = useToast();
  const [deposits, setDeposits] = useState([]);
  const [filteredDeposits, setFilteredDeposits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedDeposit, setSelectedDeposit] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [adminNotes, setAdminNotes] = useState('');
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetchDeposits();
    fetchStats();
  }, [statusFilter]);

  const fetchDeposits = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const endpoint = statusFilter === 'all'
        ? '/api/admin/deposits/all'
        : statusFilter === 'pending' 
        ? '/api/admin/deposits/pending'
        : `/api/admin/deposits/all?status=${statusFilter}`;
      
      const response = await axios.get(`${API_URL}${endpoint}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setDeposits(response.data.deposits);
        setFilteredDeposits(response.data.deposits);
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to fetch deposits',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API_URL}/api/admin/deposits/stats/summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setStats(response.data.stats);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleAction = async (depositId, action) => {
    setActionLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.post(
        `${API_URL}/api/admin/deposits/${depositId}/action`,
        {
          action: action,
          admin_notes: adminNotes || undefined
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        toast({
          title: 'Success',
          description: response.data.message,
        });
        
        setShowModal(false);
        setSelectedDeposit(null);
        setAdminNotes('');
        fetchDeposits();
        fetchStats();
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to process deposit',
        variant: 'destructive'
      });
    } finally {
      setActionLoading(false);
    }
  };

  const viewDetails = (deposit) => {
    setSelectedDeposit(deposit);
    setShowModal(true);
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50',
      completed: 'bg-green-500/20 text-green-400 border-green-500/50',
      rejected: 'bg-red-500/20 text-red-400 border-red-500/50'
    };

    const icons = {
      pending: <Clock className="w-4 h-4" />,
      completed: <CheckCircle className="w-4 h-4" />,
      rejected: <XCircle className="w-4 h-4" />
    };

    return (
      <span className={`flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold border ${styles[status] || styles.pending}`}>
        {icons[status]}
        {status.toUpperCase()}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-6">
      <div className="container mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Deposit Management</h1>
          <p className="text-gray-400">Review and process user deposit requests</p>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-slate-800/50 p-4 rounded-lg border border-yellow-500/30">
              <div className="text-sm text-gray-400">Pending</div>
              <div className="text-2xl font-bold text-yellow-400">{stats.pending_count}</div>
            </div>
            <div className="bg-slate-800/50 p-4 rounded-lg border border-green-500/30">
              <div className="text-sm text-gray-400">Approved</div>
              <div className="text-2xl font-bold text-green-400">{stats.approved_count}</div>
            </div>
            <div className="bg-slate-800/50 p-4 rounded-lg border border-red-500/30">
              <div className="text-sm text-gray-400">Rejected</div>
              <div className="text-2xl font-bold text-red-400">{stats.rejected_count}</div>
            </div>
            <div className="bg-slate-800/50 p-4 rounded-lg border border-cyan-500/30">
              <div className="text-sm text-gray-400">Total Approved</div>
              <div className="text-2xl font-bold text-cyan-400">${stats.total_approved_amount.toFixed(2)}</div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex items-center gap-4 mb-6">
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-gray-400" />
            <span className="text-sm text-gray-400">Filter:</span>
          </div>
          
          {['all', 'pending', 'completed', 'rejected'].map(status => (
            <button
              key={status}
              onClick={() => setStatusFilter(status)}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                statusFilter === status
                  ? 'bg-cyan-500 text-white'
                  : 'bg-slate-800 text-gray-400 hover:bg-slate-700'
              }`}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </button>
          ))}

          <button
            onClick={() => {
              fetchDeposits();
              fetchStats();
            }}
            className="ml-auto flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 rounded-lg text-white transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>

        {/* Deposits Table */}
        <div className="bg-slate-800/50 rounded-lg border border-slate-700 overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-gray-400">Loading deposits...</div>
          ) : filteredDeposits.length === 0 ? (
            <div className="p-8 text-center text-gray-400">No deposits found</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-900/50 border-b border-slate-700">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400">User</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400">Currency</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400">Amount</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400">Network</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDeposits.map((deposit) => (
                    <tr key={deposit.deposit_id} className="border-b border-slate-700 hover:bg-slate-800/30">
                      <td className="px-4 py-4">
                        <div>
                          <div className="text-white font-semibold">{deposit.username}</div>
                          <div className="text-xs text-gray-400">{deposit.email}</div>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <span className="font-mono font-bold text-cyan-400">{(deposit.currency || '').toUpperCase()}</span>
                      </td>
                      <td className="px-4 py-4">
                        <span className="font-bold text-white">{deposit.amount}</span>
                      </td>
                      <td className="px-4 py-4">
                        <span className="text-sm text-gray-300">{deposit.network}</span>
                      </td>
                      <td className="px-4 py-4">
                        <span className="text-sm text-gray-300">
                          {new Date(deposit.created_at).toLocaleDateString()}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        {getStatusBadge(deposit.status)}
                      </td>
                      <td className="px-4 py-4">
                        <button
                          onClick={() => viewDetails(deposit)}
                          className="flex items-center gap-2 px-3 py-1 bg-cyan-500/10 border border-cyan-500/30 rounded text-cyan-400 hover:bg-cyan-500/20 transition-colors text-sm"
                        >
                          <Eye className="w-4 h-4" />
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Detail Modal */}
      {showModal && selectedDeposit && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-900 rounded-xl border border-slate-700 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-white">Deposit Details</h2>
                <button
                  onClick={() => {
                    setShowModal(false);
                    setSelectedDeposit(null);
                    setAdminNotes('');
                  }}
                  className="text-gray-400 hover:text-white"
                >
                  ✕
                </button>
              </div>

              {/* Deposit Info */}
              <div className="space-y-4 mb-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-sm text-gray-400">Deposit ID</div>
                    <div className="font-mono text-white">{selectedDeposit.deposit_id}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-400">Status</div>
                    <div>{getStatusBadge(selectedDeposit.status)}</div>
                  </div>
                </div>

                <div className="border-t border-slate-700 pt-4">
                  <div className="text-sm text-gray-400 mb-2">User Information</div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-xs text-gray-500">Username</div>
                      <div className="text-white">{selectedDeposit.username}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Email</div>
                      <div className="text-white">{selectedDeposit.email}</div>
                    </div>
                  </div>
                </div>

                <div className="border-t border-slate-700 pt-4">
                  <div className="text-sm text-gray-400 mb-2">Deposit Information</div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-xs text-gray-500">Currency</div>
                      <div className="font-mono font-bold text-cyan-400">{(selectedDeposit.currency || '').toUpperCase()}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Network</div>
                      <div className="text-white">{selectedDeposit.network}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Amount</div>
                      <div className="text-2xl font-bold text-white">{selectedDeposit.amount}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Date</div>
                      <div className="text-white">{new Date(selectedDeposit.created_at).toLocaleString()}</div>
                    </div>
                  </div>
                </div>

                {selectedDeposit.screenshot_path && (
                  <div className="border-t border-slate-700 pt-4">
                    <div className="text-sm text-gray-400 mb-2">Payment Screenshot</div>
                    <img 
                      src={`${API_URL}${selectedDeposit.screenshot_path}`}
                      alt="Payment Screenshot"
                      className="max-w-full rounded-lg border border-slate-700"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                )}
              </div>

              {/* Admin Actions (only for pending deposits) */}
              {selectedDeposit.status === 'pending' && (
                <div className="border-t border-slate-700 pt-4">
                  <div className="text-sm text-gray-400 mb-3">Admin Notes (Optional)</div>
                  <textarea
                    value={adminNotes}
                    onChange={(e) => setAdminNotes(e.target.value)}
                    placeholder="Add notes about this deposit..."
                    className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-gray-500 focus:border-cyan-500 focus:outline-none mb-4"
                    rows="3"
                  />

                  <div className="flex gap-3">
                    <button
                      onClick={() => handleAction(selectedDeposit.deposit_id, 'approve')}
                      disabled={actionLoading}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-green-500 hover:bg-green-600 text-white font-semibold rounded-lg transition-colors disabled:opacity-50"
                    >
                      <CheckCircle className="w-5 h-5" />
                      {actionLoading ? 'Processing...' : 'Approve & Credit'}
                    </button>
                    <button
                      onClick={() => handleAction(selectedDeposit.deposit_id, 'reject')}
                      disabled={actionLoading}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-red-500 hover:bg-red-600 text-white font-semibold rounded-lg transition-colors disabled:opacity-50"
                    >
                      <XCircle className="w-5 h-5" />
                      {actionLoading ? 'Processing...' : 'Reject'}
                    </button>
                  </div>
                </div>
              )}

              {/* Processed Info (for completed/rejected) */}
              {selectedDeposit.status !== 'pending' && (
                <div className="border-t border-slate-700 pt-4">
                  <div className="text-sm text-gray-400 mb-2">Processing Information</div>
                  <div className="space-y-2 text-sm">
                    {selectedDeposit.processed_by_username && (
                      <div>
                        <span className="text-gray-500">Processed by:</span>
                        <span className="text-white ml-2">{selectedDeposit.processed_by_username}</span>
                      </div>
                    )}
                    {selectedDeposit.processed_at && (
                      <div>
                        <span className="text-gray-500">Processed at:</span>
                        <span className="text-white ml-2">{new Date(selectedDeposit.processed_at).toLocaleString()}</span>
                      </div>
                    )}
                    {selectedDeposit.admin_notes && (
                      <div>
                        <div className="text-gray-500">Admin Notes:</div>
                        <div className="text-white bg-slate-800 p-2 rounded mt-1">{selectedDeposit.admin_notes}</div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DepositManagement;
